import { primitiveTokens, semanticThemeTokens } from "@appretio/ui-kit";
import { globalStyle, style } from "@vanilla-extract/css";

// ---------------------------------------------------------------------------
// Shared across all cube pages (main + summary)
// ---------------------------------------------------------------------------

export const contextContainer = style({
  display: "flex",
  justifyContent: "space-between"
});

export const contextActionsContainer = style({
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  justifyContent: "center",
  gap: primitiveTokens.sizing["1x"],
  paddingRight: primitiveTokens.sizing["3x"],
  borderRight: `1px solid ${semanticThemeTokens.button.secondary.default.borderColor}`
});

export const contextSelectorsContainer = style({
  flexGrow: 1,
  // Without this the column refuses to go narrower than its chips' text once
  // they carry values, and the rows overflow the sheet.
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  paddingLeft: primitiveTokens.sizing["3x"],
  gap: primitiveTokens.sizing["1x"]
});

/** The dimension dropdowns with the Context picker that names them at the end. */
export const contextRow = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["1x"]
});

/** The dropdowns yield to the picker, and truncate rather than overflow. */
export const contextDimensions = style({
  flex: "1 1 auto",
  minWidth: 0
});

export const tableContent = style({
  display: "flex",
  flexDirection: "column",
  gap: primitiveTokens.sizing["2x"],
  minHeight: "100%"
});

// ---------------------------------------------------------------------------
// Main pages (forecast + budget)
// ---------------------------------------------------------------------------

export const contextVersionActionsContainer = style({
  display: "grid",
  gridTemplateColumns: "minmax(0, 2fr) auto minmax(0, 1fr)",
  alignItems: "center",
  gap: primitiveTokens.sizing["1x"]
});

export const contextMessage = style({
  textAlign: "right",
  fontSize: "12px",
  whiteSpace: "nowrap"
});

export const tableControlsContainer = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["2x"],
  justifyContent: "space-between"
});

export const tableActionButtons = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["2x"]
});

// The grid block is exactly one sheet tall, so once the sheet scrolls past
// the graph, the controls and the grid's header row stay in view.
export const temporalGridContent = style({
  display: "flex",
  flexDirection: "column",
  gap: primitiveTokens.sizing["2x"],
  height: "100%"
});

// Auto height, so the chart's height:100% keeps its natural size.
export const graphStack = style({
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  gap: primitiveTokens.sizing["2x"],
  selectors: {
    // Otherwise the parent's gap still spaces the hidden graph.
    "&:empty": { display: "none" }
  }
});

export const temporalGridBlock = style({
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  gap: primitiveTokens.sizing["2x"],
  height: "100%"
});

export const dataTableWrapper = style({
  flex: 1,
  minHeight: "300px",
  position: "relative"
});

// AG Grid's root sizes itself with height:100%. WebKit won't resolve that
// against a parent whose height comes from flex sizing rather than a definite
// `height`, so in Safari the grid collapsed to 0 and rendered empty. Absolute
// positioning takes the height from the (already relative) wrapper instead.
globalStyle(`${dataTableWrapper} > *`, {
  position: "absolute",
  inset: 0
});

// ---------------------------------------------------------------------------
// Summary pages (forecast summary + budget summary)
// ---------------------------------------------------------------------------

export const controlsRow = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["2x"]
});

/**
 * Wrapper for the table-dim / row-dim pair. Hosts a ``<SelectGroup>`` so
 * the two chips read as a glued pair, mirroring the version triples.
 * Flex weight is proportional to the two children inside (vs. three in
 * each version group), which keeps every chip in the row roughly the
 * same width.
 */
export const dimSelectorsContainer = style({
  display: "flex",
  alignItems: "center",
  flex: 2
});

/**
 * Wrapper for a producer/version/year (or index-) triple. Hosts a
 * ``<SelectGroup>`` that visually glues the three chips together, so this
 * wrapper itself contributes no internal gap — the SelectGroup handles
 * layout. Flex weight matches the three children for equal per-chip width
 * with the dim group.
 */
export const versionSelectorsContainer = style({
  display: "flex",
  alignItems: "center",
  flex: 3
});

/**
 * Wrapper for the standalone Store Status filter on the right of the
 * controls row. One-child flex weight keeps it the same per-chip width as
 * the items in the version groups.
 */
export const storeStatusContainer = style({
  display: "flex",
  alignItems: "center",
  flex: 1
});

export const graphControlsRow = style({
  display: "flex",
  alignItems: "center",
  gap: primitiveTokens.sizing["2x"],
  maxWidth: "400px"
});

/**
 * Summary page: only ``summaryScrollArea`` scrolls, so the table controls
 * stay put. A sticky bar can't mask the rows — sheet layers are translucent.
 */
export const summaryContent = style({
  height: "100%"
});

export const summaryScrollArea = style({
  display: "flex",
  flexDirection: "column",
  gap: primitiveTokens.sizing["2x"],
  flex: 1,
  minHeight: 0,
  overflowY: "auto"
});

export const emptyState = style({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: "200px",
  opacity: 0.6,
  fontSize: "14px"
});

export const savedAtLabel = style({
  fontSize: "12px",
  opacity: 0.7
});

export const gridFilterPending = style({
  opacity: 0.6,
  pointerEvents: "none"
});

export const gridReady = style({
  opacity: 1,
  transition: "opacity 0.2s ease-out",
  pointerEvents: "auto"
});

// ---------------------------------------------------------------------------
// Manage cubes pages
// ---------------------------------------------------------------------------

export const manageCubesSelectorRow = style({
  display: "flex",
  gap: primitiveTokens.sizing["4x"],
  flexWrap: "wrap"
});

/**
 * Let direct children share the row evenly — the forecast page drops a
 * single labeled SingleSelect here (fills the whole row); the budget page
 * drops two (split 50/50). ``flex-wrap: wrap`` on the parent still lets
 * them break onto separate lines when the panel narrows past the 240px
 * flex-basis.
 */
globalStyle(`${manageCubesSelectorRow} > *`, {
  flex: "1 1 240px",
  minWidth: 0
});

export const manageCubesEmptyState = style({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%"
});
